import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import { Button } from './Button'

// ── Icons ──────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

/** Props for the {@link PresentationActionModal} confirmation dialog. */
export interface PresentationActionModalProps {
  title: string
  confirmLabel: string
  /** Show a "Send Notification" checkbox (accept / reject flows). */
  showNotify: boolean
  /** Show a free-text message textarea (reject-single / ask flows). */
  showMessage: boolean
  messageLabel?: string
  messagePlaceholder?: string
  onConfirm: (result: { notify: boolean; message: string }) => void
  onCancel: () => void
}

/**
 * Flexible confirmation dialog for presentation actions (ask / accept / reject).
 * Separates the concerns of the rigid `ConfirmationModal` by accepting explicit
 * props for which controls to render rather than inferring from data shape.
 */
export function PresentationActionModal({
  title,
  confirmLabel,
  showNotify,
  showMessage,
  messageLabel,
  messagePlaceholder,
  onConfirm,
  onCancel,
}: PresentationActionModalProps) {
  const { t } = useTranslation()
  const [notify, setNotify] = useState(showNotify)
  const [message, setMessage] = useState('')

  function handleConfirm() {
    onConfirm({ notify, message })
  }

  return (
    <Modal labelledBy="pres-action-modal-title">
      <div className="border-pax-purple/50 relative w-full max-w-md rounded-2xl border bg-white p-6 shadow-2xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={onCancel}
          className="text-pax-gray hover:text-pax-black absolute top-4 right-4 cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h2 id="pres-action-modal-title" className="text-pax-black mb-5 pr-8 text-xl font-bold">
          {title}
        </h2>

        {showMessage && (
          <div className="mb-4 flex flex-col gap-1">
            <label htmlFor="pres-action-message" className="text-pax-black text-sm font-medium">
              {messageLabel ?? t('pres_message_label')}
            </label>
            <textarea
              id="pres-action-message"
              className="border-pax-purple/40 bg-pax-light-purple text-pax-black focus:border-pax-blue min-h-[80px] w-full resize-y rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={messagePlaceholder ?? ''}
            />
          </div>
        )}

        {showNotify && (
          <label className="mb-6 flex cursor-pointer items-center gap-2.5 text-sm select-none">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="accent-pax-blue h-4 w-4 cursor-pointer"
            />
            <span className="text-pax-black">{t('send_notification')}</span>
          </label>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button variant="secondary" onClick={onCancel}>
            {t('btn_cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
